import { useEffect, useState } from "react"
import { useParams, Link, Navigate } from "react-router-dom"
import { PageHeader, PageSpinner } from "@/components/page"
import { ChevronLeft, GraduationCap, MapPin, Maximize2, RefreshCw, Rocket, Shield, Users } from "lucide-react"
import { useCccdTeamGallery } from "../hooks/useCccdTeamGallery"
import { useCccdEvents } from "../hooks/useCccdEvents"
import { useBreadcrumbStore } from "../stores/breadcrumbStore"
import { getContestCategoryContent } from "../data/cccdContestCategories"
import { initials, locationLine, ROLE_LABEL } from "../components/cccd/teamUtils"
import { PhotoLightbox, type LightboxItem } from "../components/cccd/PhotoLightbox"

// Read-only public profile of ANOTHER competing team (not your own group) —
// same photo-hero language as the group's own dashboard (GroupDashboard),
// but sourced from the event-scoped gallery, so it only ever exposes what
// every competitor already sees in the gallery cards (name, category,
// institution, member first/last name + photo — never email).
export default function CccdTeamDetailPage() {
  const { eventId, teamId } = useParams<{ eventId: string; teamId: string }>()
  const { teams, isLoading, error, refetch } = useCccdTeamGallery(eventId ?? null)
  const { nationalEvents, regionalEvents } = useCccdEvents()
  const setBreadcrumbLabel = useBreadcrumbStore((s) => s.setLabel)
  // Visor de fotos completas (integrantes o foto grupal); null = cerrado.
  const [viewer, setViewer] = useState<{ items: LightboxItem[]; index: number } | null>(null)

  const team = teams.find((t) => t.id === teamId)
  const event = [...nationalEvents, ...regionalEvents].find((e) => e.id === eventId)

  useEffect(() => {
    if (event) setBreadcrumbLabel(`/cccd/competidores/${eventId}`, event.name)
  }, [event, eventId, setBreadcrumbLabel])

  useEffect(() => {
    if (team && eventId) setBreadcrumbLabel(`/cccd/competidores/${eventId}/equipos/${team.id}`, team.name)
  }, [team, eventId, setBreadcrumbLabel])

  if (isLoading) {
    return (
      <PageSpinner />
    )
  }

  if (error) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card p-12 text-center">
        <p className="text-sm text-destructive">{error}</p>
        <button
          type="button"
          onClick={refetch}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-semibold text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Reintentar
        </button>
      </div>
    )
  }

  if (!team) {
    return <Navigate to={`/cccd/competidores/${eventId}`} replace />
  }

  const categoryTitle = getContestCategoryContent(team.categorySlug)?.title ?? "Categoría sin asignar"

  const memberFullName = (m: { firstName: string | null; lastName: string | null }) =>
    `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim() || "Integrante"
  // Solo los integrantes con foto entran al visor; el índice se calcula sobre esa lista.
  const photoMembers = team.members.filter((m) => m.photoUrl)
  const memberPhotoItems: LightboxItem[] = photoMembers.map((m) => ({
    src: m.photoUrl as string,
    alt: `Foto de ${memberFullName(m)}, integrante del equipo ${team.name}`,
    title: memberFullName(m),
    subtitle: ROLE_LABEL[m.role] ?? null,
  }))

  return (
    <div className="space-y-8">
      <Link
        to={`/cccd/competidores/${eventId}`}
        className="inline-flex items-center gap-1.5 rounded-md text-sm font-semibold text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2"
      >
        <ChevronLeft className="h-4 w-4" /> Volver a Equipos
      </Link>

      <div className="relative overflow-hidden rounded-xl border border-border" style={{ height: 280 }}>
        {team.groupPhotoUrl ? (
          <img src={team.groupPhotoUrl} alt={`Foto del equipo ${team.name}`} loading="eager" decoding="async" className="absolute inset-0 h-full w-full object-cover object-[center_30%]" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <Users className="h-16 w-16 text-muted-foreground/40" aria-hidden="true" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute left-6 top-5 inline-flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5">
          <Users className="h-3 w-3 text-white" />
          <span className="text-micro font-bold uppercase tracking-wide text-white">
            {team.scope === "national" ? "CCCD Nacional" : "CCCD Regional"} · {categoryTitle}
          </span>
        </div>
        {team.groupPhotoUrl && (
          <button
            type="button"
            onClick={() =>
              setViewer({
                items: [
                  {
                    src: team.groupPhotoUrl as string,
                    alt: `Foto de grupo del equipo ${team.name}`,
                    title: team.name,
                    subtitle: "Foto del equipo completa",
                  },
                ],
                index: 0,
              })
            }
            className="absolute right-4 top-4 inline-flex min-h-9 items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-xs font-semibold text-white outline-none transition-colors hover:bg-black/85 focus-visible:ring-2 focus-visible:ring-white [@media(pointer:coarse)]:min-h-11"
          >
            <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" /> Ver foto completa
          </button>
        )}
        <div className="absolute bottom-5 left-6 right-6">
          <PageHeader variant="cover" title={team.name} />
        </div>
      </div>

      <div className="flex items-center gap-4 rounded-xl border border-border bg-card px-5 py-4">
        {/* Rounded-square holder, not a circular avatar — a patch can be any
            shape (shield, star, custom emblem), shown whole via object-contain. */}
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-background p-1.5">
          {team.patchImageUrl ? (
            <img src={team.patchImageUrl} alt={`Parche de ${team.name}`} className="h-full w-full object-contain" />
          ) : (
            <Shield className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
        {(team.institutionName || locationLine(team.institutionCity, team.institutionCountry)) && (
          <div className="min-w-0 space-y-1">
            {team.institutionName && (
              <div className="flex items-center gap-1.5">
                <GraduationCap className="h-4 w-4 shrink-0 text-primary" />
                <span className="truncate text-sm font-semibold text-foreground">{team.institutionName}</span>
              </div>
            )}
            {locationLine(team.institutionCity, team.institutionCountry) && (
              <div className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm text-muted-foreground">
                  {locationLine(team.institutionCity, team.institutionCountry)}
                </span>
              </div>
            )}
          </div>
        )}
        {team.motor && (
          <div className="ml-auto flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5">
            <Rocket className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="text-xs font-semibold text-primary">Motor {team.motor}</span>
          </div>
        )}
      </div>

      <section>
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground">
          <Users className="h-5 w-5 text-primary" /> Participantes del equipo
        </h2>
        {team.members.length === 0 ? (
          <p className="text-sm text-muted-foreground">Este equipo aún no tiene participantes públicos.</p>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {team.members.map((m) => {
              const name = memberFullName(m)
              const photoIndex = m.photoUrl ? photoMembers.findIndex((p) => p.id === m.id) : -1
              return (
                <li key={m.id} className="overflow-hidden rounded-xl border border-border bg-card">
                  {/* Retrato 3:4 con object-contain: la foto se ve completa, sin recortar
                      cabeza ni cuerpo; lo que sobra se rellena con el tono de la superficie. */}
                  {m.photoUrl ? (
                    <button
                      type="button"
                      onClick={() => setViewer({ items: memberPhotoItems, index: photoIndex })}
                      aria-label={`Ver foto completa de ${name}`}
                      className="group relative flex aspect-[3/4] w-full items-center justify-center bg-muted outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    >
                      <img
                        src={m.photoUrl}
                        alt={`Foto de ${name}, integrante del equipo ${team.name}`}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-contain"
                      />
                      <span className="pointer-events-none absolute bottom-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white opacity-0 transition-opacity duration-(--dur-fast) group-hover:opacity-100 group-focus-visible:opacity-100 [@media(pointer:coarse)]:opacity-100">
                        <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </span>
                    </button>
                  ) : (
                    <div className="flex aspect-[3/4] w-full items-center justify-center bg-muted" aria-hidden="true">
                      <span className="text-3xl font-semibold text-muted-foreground">
                        {initials(m.firstName, m.lastName)}
                      </span>
                    </div>
                  )}
                  <div className="p-3">
                    <p className="line-clamp-2 font-semibold text-foreground" title={name}>
                      {name}
                    </p>
                    {ROLE_LABEL[m.role] && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{ROLE_LABEL[m.role]}</p>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <PhotoLightbox
        items={viewer?.items ?? []}
        index={viewer ? viewer.index : null}
        onIndexChange={(next) =>
          setViewer((v) => (v && next !== null ? { ...v, index: next } : null))
        }
      />
    </div>
  )
}
